import React, { PropsWithChildren } from "react";
import { Icon, List, Image, Responsive } from "semantic-ui-react";
import ImageInput from "../../../sg-fileupload/components/ImageInput";
import { IFileUploadElementProps } from "../../../sg-fileupload/components/FileUpload";
import { IImage } from "../../interfaces/IImage";
import { useTranslation } from "react-i18next";


const DefaultInputElement: React.FC<PropsWithChildren<IFileUploadElementProps>> = ({ children, isDragActive }) => {
    const { t } = useTranslation()
    const texts = {
        dragImageHereOrClick: t("Lataa kuva raahaamalla se tähän tai napsauttamalla")
    };

    return (
        <Responsive>
            <div className={`drop zone ${isDragActive && "loading"}`}>
                <div className="drop icon">
                    <Icon.Group size="big">
                        <Icon name="circle outline" size="large" fitted color="grey" />
                        <Icon name="camera" size="small" color="grey" />
                    </Icon.Group>
                </div>
                <div className="drop text">
                    {texts.dragImageHereOrClick}
                    {children}
                </div>
            </div>
        </Responsive>
    );
};

type TValue = IImage | IImage[];

type TValueProp<T = IImage> = T extends [] ? IImage[] : IImage;


interface ImageUploadProps<T extends TValue> {
    type?: string,
    value?: TValueProp<T>;
    onChange: (value: TValueProp<T>) => void;
    readonly?: boolean;
    disabled?: boolean;
    isMulti?: boolean;
    InputElement?: React.FC<PropsWithChildren<IFileUploadElementProps>>
}

const ImageUpload = <T extends TValue>(props: ImageUploadProps<T>) => {
    const {
        value,
        onChange,
        disabled = false,
        readonly = false,
        isMulti = false,
        InputElement = DefaultInputElement
    } = props


    const onAddImage = (newImages: IImage[]) => {
        if (!onChange) return;
        const prevValue = isMulti ? (Array.isArray(value) ? value || [] as IImage[] : [] as IImage[]) : value;
        if (isMulti) {
            onChange((prevValue as any).concat(newImages));
        } else {
            onChange(newImages[0] as any)
        }
    };

    const onRemovedImage = (guid: string) => {
        if (!onChange) return;
        if (isMulti) {
            onChange((value as any || []).filter((file: any) => file.id !== guid))
        } else {
            onChange(null as any)
        }
    };

    if (readonly) {
        return (
            <List className="ImageList" horizontal>
                {isMulti ? (
                    (value as any || []).map((file: any) => (
                        <List.Item key={file.id}>
                            <Image
                                className="ImageList_img"
                                id={file.id}
                            />
                        </List.Item>
                    ))
                ) : value ? (
                    <List.Item>
                        <Image
                            className="ImageList_img"
                            id={value.id}
                        />
                    </List.Item>
                ) : null}
            </List>
        )
    }

    return (
        <ImageInput
            multiple={isMulti}
            inputAreaElem={InputElement}
            onUploaded={onAddImage}
            onRemoved={onRemovedImage}
            files={isMulti && Array.isArray(value) ? value : !isMulti && value ? [value] : []}
            disabled={disabled || readonly}
        />
    );
}

ImageUpload.defaultProps = {
    type: "text",
    required: false,
    disabled: false,
    readOnly: false,
    autoFocus: false,
};

export default ImageUpload;
